/**
 * Backend-aligned API paths. All paths relative to API base (e.g. /api/v1).
 * Use with apiJson/apiFetch. Do not change backend; only frontend adapts.
 */

/** Users (access: role, permissions, modules — USER_MANAGEMENT / PERMISSION_MANAGEMENT) */
export const API_USERS = {
  patchRole: (id: string) => `/api/users/${id}/role`,
  patchPermissions: (id: string) => `/api/users/${id}/permissions`,
  patchModules: (id: string) => `/api/users/${id}/modules`,
} as const;

/** Auth */
export const API_AUTH = {
  requestOtp: "/api/auth/request-otp",
  verifyOtp: "/api/auth/verify-otp",
  verifyOtpForRegistration: "/api/auth/verify-otp-for-registration",
  supportGenerateOtp: "/api/auth/support/generate-otp",
  me: "/api/auth/me",
  permissionsVersion: "/api/auth/permissions-version",
  permissionsStream: "/api/auth/permissions-stream",
  sseToken: "/api/auth/sse-token",
  notificationPreferences: "/api/auth/notification-preferences",
  logout: "/api/auth/logout",
  refresh: "/api/auth/refresh",
  fcmToken: "/api/auth/fcm-token",
} as const;

/** Members: registration, profile, id card */
export const API_MEMBERS = {
  register: "/api/members/register",
  registerWithOtp: "/api/members/register-with-otp",
  draft: "/api/members/draft",
  profile: "/api/members/profile",
  mePlan: "/api/members/me/plan",
  meInvoices: "/api/members/me/invoices",
  uploadUrl: "/api/members/upload-url",
  idcardPdf: (memberId: string) => `/api/members/${memberId}/idcard/pdf`,
  idcardFront: (memberId: string) => `/api/members/${memberId}/idcard/front.png`,
  idcardBack: (memberId: string) => `/api/members/${memberId}/idcard/back.png`,
} as const;

/** User panel (Association member) — spec paths under /api/v1/user, role=USER only */
export const API_USER = {
  profile: "/api/user/profile",
  profilePhotoUploadUrl: "/api/user/profile/photo/upload-url",
  shop: "/api/user/shop",
  shopGalleryUploadUrl: "/api/user/shop/gallery/upload-url",
  shopGallery: "/api/user/shop/gallery",
  shopClaimApprove: (claimId: string) => `/api/user/shop/claims/${claimId}/approve`,
  shopClaimReject: (claimId: string) => `/api/user/shop/claims/${claimId}/reject`,
  shopClaimRequest: "/api/user/shop/claim-request",
  shopClaimStatus: "/api/user/shop/claim-status",
  shopGalleryPublish: (photoId: string) => `/api/user/shop/gallery/${photoId}/publish`,
  subscription: "/api/user/subscription",
  invoices: "/api/user/invoices",
  invoiceDownload: (invoiceId: string) => `/api/user/invoices/${invoiceId}/download`,
  tickets: "/api/user/tickets",
  ticketDetail: (ticketNo: string) => `/api/user/tickets/${ticketNo}`,
  ticketReply: (ticketNo: string) => `/api/user/tickets/${ticketNo}/reply`,
  announcements: "/api/user/announcements",
  announcementRead: (id: string) => `/api/user/announcements/${id}/read`,
  logsSelf: "/api/user/logs/self",
} as const;

/** Public (no auth) */
export const API_PUBLIC = {
  health: "/api/public/health",
  registrationStatus: "/api/public/registration-status",
  member: (memberId: string) => `/api/public/member/${memberId}`,
  members: (memberId: string) => `/api/public/members/${memberId}`,
  shops: "/api/public/shops",
  /** Shop autocomplete during registration (no JWT). */
  shopsSuggest: "/api/public/shops/suggest",
  securityQuestions: "/api/public/security-questions",
  payCta: "/api/public/pay/cta",
  announcements: "/api/public/announcements",
  polls: "/api/public/polls",
  ticketStatus: "/api/public/tickets/status",
  ticketTrack: "/api/public/tickets/track",
  ticketUpload: "/api/public/tickets/upload",
  ticketCreate: "/api/public/tickets",
  ticketAttachmentDownload: (attachmentId: string, ticketCode: string, secretKey: string) =>
    `/api/public/tickets/attachments/${attachmentId}/download?ticketCode=${encodeURIComponent(ticketCode)}&secretKey=${encodeURIComponent(secretKey)}`,
} as const;

/** Tickets: public categories + member-facing (auth) */
export const API_TICKETS = {
  categories: "/api/tickets/categories",
  /** List current user's tickets */
  me: "/api/tickets/me",
  /** Create ticket (auth) */
  create: "/api/tickets",
  detail: (code: string) => `/api/tickets/${code}`,
  reply: (code: string) => `/api/tickets/${code}/reply`,
  attachmentDownload: (code: string, attachmentId: string) =>
    `/api/tickets/${code}/attachments/${attachmentId}/download`,
} as const;

/** Billing: plans, my-plan, pay-token, orders, invoices (auth where noted) */
export const API_BILLING = {
  plans: "/api/billing/plans",
  myPlan: "/api/billing/my-plan",
  payToken: "/api/billing/pay-token",
  validateCoupon: "/api/billing/validate-coupon",
  subscription: "/api/billing/subscription",
  invoices: "/api/billing/invoices",
  invoiceDownload: (invoiceId: string) => `/api/billing/invoices/${invoiceId}/download`,
  createOrder: "/api/billing/orders",
  checkout: "/api/billing/checkout",
  orderStatus: (orderCode: string) => `/api/billing/orders/${orderCode}/status`,
  orderDetail: (orderCode: string) => `/api/billing/orders/${orderCode}`,
  paymentStatus: (paymentId: string) => `/api/billing/payment/${paymentId}/status`,
} as const;

/** Member-facing polls: vote and results (auth for vote) */
export const API_POLLS_MEMBER = {
  results: (id: string) => `/api/polls/${id}/results`,
  vote: (id: string) => `/api/polls/${id}/vote`,
} as const;

/** Admin: dashboard, members, users, shops, exports, settings */
export const API_ADMIN = {
  base: "/api/admin",
  check: "/api/admin/check",
  kpis: "/api/admin/kpis",
  recentActivity: "/api/admin/recent-activity",
  readiness: "/api/admin/readiness",
  membersList: "/api/admin/members/list",
  membersTrash: "/api/admin/members/trash",
  memberDetail: (id: string) => `/api/admin/members/${id}`,
  memberPatch: (id: string) => `/api/admin/members/${id}`,
  memberSuspend: (id: string) => `/api/admin/members/${id}/suspend`,
  memberUnsuspend: (id: string) => `/api/admin/members/${id}/unsuspend`,
  memberPermanentSuspend: (id: string) => `/api/admin/members/${id}/permanent-suspend`,
  memberTrash: (id: string) => `/api/admin/members/${id}/trash`,
  memberRestore: (id: string) => `/api/admin/members/${id}/restore`,
  memberPermanentDelete: (id: string) => `/api/admin/members/${id}/permanent-delete`,
  verificationQueue: "/api/admin/members/verification-queue",
  pendingVerification: "/api/admin/members/pending-verification",
  memberVerify: (id: string) => `/api/admin/members/${id}/verify`,
  membersApprovalQueue: "/api/admin/members/approval-queue",
  membersPendingApproval: "/api/admin/members/pending-approval",
  memberApprove: (id: string) => `/api/admin/members/${id}/approve`,
  memberActivateWithoutPayment: (id: string) => `/api/admin/members/${id}/activate-without-payment`,
  memberReject: (id: string) => `/api/admin/members/${id}/reject`,
  shopsList: "/api/admin/shops/list",
  shopsClaims: "/api/admin/shops/claims",
  shopsChangeRequestsQueue: "/api/admin/shops/change-requests-queue",
  shopDetail: (id: string) => `/api/admin/shops/${id}`,
  shopPatch: (id: string) => `/api/admin/shops/${id}`,
  shopChangeRequestVerify: (requestId: string) => `/api/admin/shops/change-requests/${requestId}/verify`,
  shopChangeRequestApprove: (requestId: string) => `/api/admin/shops/change-requests/${requestId}/approve`,
  shopChangeRequestReject: (requestId: string) => `/api/admin/shops/change-requests/${requestId}/reject`,
  shopClaimApprove: (shopId: string, claimId: string) => `/api/admin/shops/${shopId}/claims/${claimId}/approve`,
  shopClaimReject: (shopId: string, claimId: string) => `/api/admin/shops/${shopId}/claims/${claimId}/reject`,
  users: "/api/admin/users",
  userDetail: (id: string) => `/api/admin/users/${id}`,
  usersCreate: "/api/admin/users",
  usersPromote: "/api/admin/users/promote",
  usersDemote: "/api/admin/users/demote",
  usersPromoteTechAdmin: "/api/admin/users/promote-tech-admin",
  userPermissions: (userId: string) => `/api/admin/users/${userId}/permissions`,
  permissionsCatalog: "/api/admin/permissions/catalog",
  exportsMembers: "/api/admin/exports/members",
  exportsShops: "/api/admin/exports/shops",
  settingsVerificationRules: "/api/admin/settings/verification-rules",
  settingsPaymentStatus: "/api/admin/settings/payment-status",
  settingsPhonepeStatus: "/api/admin/settings/phonepe-status",
  automationsRunExpiry: "/api/admin/automations/run-expiry",
  automationsRules: "/api/admin/automations/rules",
  automationsRulesPatch: (id: string) => `/api/admin/automations/rules/${id}`,
  automationsDispatch: "/api/admin/automations/dispatch",
  automationsDryRun: "/api/admin/automations/dry-run",
} as const;

/** Admin tickets */
export const API_ADMIN_TICKETS = {
  list: "/api/admin/tickets",
  assignableUsers: "/api/admin/tickets/assignable-users",
  categories: "/api/admin/tickets/categories",
  createCategory: "/api/admin/tickets/categories",
  patchCategory: (id: string) => `/api/admin/tickets/categories/${id}`,
  deleteCategory: (id: string) => `/api/admin/tickets/categories/${id}`,
  detail: (code: string) => `/api/admin/tickets/${code}`,
  attachmentDownload: (code: string, attachmentId: string) =>
    `/api/admin/tickets/${code}/attachments/${attachmentId}/download`,
  reply: (code: string) => `/api/admin/tickets/${code}/reply`,
  status: (code: string) => `/api/admin/tickets/${code}/status`,
  close: (code: string) => `/api/admin/tickets/${code}/close`,
  assign: (code: string) => `/api/admin/tickets/${code}/assign`,
} as const;

/** Admin email change (recovery) requests */
export const API_ADMIN_RECOVERY = {
  list: "/api/admin/email-change-requests",
  detail: (code: string) => `/api/admin/email-change-requests/${code}`,
  approve: (code: string) => `/api/admin/email-change-requests/${code}/approve`,
  reject: (code: string) => `/api/admin/email-change-requests/${code}/reject`,
  sendNewEmailOtp: (code: string) => `/api/admin/email-change-requests/${code}/send-new-email-otp`,
  execute: (code: string) => `/api/admin/email-change-requests/${code}/execute`,
} as const;

/** Recovery (public): email change request flow */
export const API_RECOVERY = {
  emailChangeRequest: "/api/recovery/email-change/request",
  emailChangeQuestions: (requestCode: string) => `/api/recovery/email-change/${requestCode}/questions`,
  emailChangeVerifySecurity: (requestCode: string) => `/api/recovery/email-change/${requestCode}/verify-security`,
  emailChangeStatus: (requestCode: string) => `/api/recovery/email-change/${requestCode}/status`,
  emailChangeVerifyNewEmailOtp: (requestCode: string) => `/api/recovery/email-change/${requestCode}/verify-new-email-otp`,
} as const;

/** Download (auth) */
export const API_DOWNLOAD = {
  idCard: "/api/download/id-card",
} as const;

/** Shops (member-facing, auth) */
export const API_SHOPS = {
  suggest: "/api/shops/suggest",
  search: "/api/shops/search",
  myShops: "/api/shops/my-shops",
  detail: (shopId: string) => `/api/shops/${shopId}`,
  patch: (shopId: string) => `/api/shops/${shopId}`,
  claim: (shopId: string) => `/api/shops/${shopId}/claim`,
  gallery: (shopId: string) => `/api/shops/${shopId}/gallery`,
  members: (shopId: string) => `/api/shops/${shopId}/members`,
  claims: (shopId: string) => `/api/shops/${shopId}/claims`,
  claimApprove: (shopId: string, claimId: string) => `/api/shops/${shopId}/claims/${claimId}/approve`,
  claimReject: (shopId: string, claimId: string) => `/api/shops/${shopId}/claims/${claimId}/reject`,
  changeRequests: (shopId: string) => `/api/shops/${shopId}/change-requests`,
  changeRequestsCreate: (shopId: string) => `/api/shops/${shopId}/change-requests`,
} as const;

/** Notifications (auth) */
export const API_NOTIFICATIONS = {
  list: "/api/notifications",
  unreadCount: "/api/notifications/unread-count",
  markRead: (id: string) => `/api/notifications/${id}/read`,
  markAllRead: "/api/notifications/read-all",
  stream: "/api/notifications/stream",
} as const;

/** Plans (public or auth as needed) */
export const API_PLANS = {
  active: "/api/plans/active",
} as const;

/** Admin announcements */
export const API_ADMIN_ANNOUNCEMENTS = {
  list: "/api/admin/announcements",
  detail: (id: string) => `/api/admin/announcements/${id}`,
  create: "/api/admin/announcements",
  patch: (id: string) => `/api/admin/announcements/${id}`,
  publish: (id: string) => `/api/admin/announcements/${id}/publish`,
  delete: (id: string) => `/api/admin/announcements/${id}`,
} as const;

/** Admin polls */
export const API_ADMIN_POLLS = {
  list: "/api/admin/polls",
  detail: (id: string) => `/api/admin/polls/${id}`,
  create: "/api/admin/polls",
  delete: (id: string) => `/api/admin/polls/${id}`,
  export: (id: string) => `/api/admin/polls/${id}/export`,
} as const;

/** Admin email templates */
export const API_ADMIN_EMAIL_TEMPLATES = {
  list: "/api/admin/email-templates",
  detail: (key: string) => `/api/admin/email-templates/${key}`,
  patch: (key: string) => `/api/admin/email-templates/${key}`,
} as const;

/** Logs (auth) */
export const API_LOGS = {
  self: "/api/logs/self",
  system: "/api/logs/system",
} as const;

/** NFC (auth, SUPER_ADMIN / TECH_ADMIN) */
export const API_NFC = {
  logs: "/api/nfc/logs",
  dashboard: "/api/nfc/dashboard",
  config: "/api/nfc/config",
} as const;

/** Cards: print batch (admin, permission-gated) */
export const API_CARDS = {
  printBatchList: "/api/cards/print-batch",
  printBatchCreate: "/api/cards/print-batch",
  printBatchDownload: (batchId: string) => `/api/cards/print-batch/${batchId}/download`,
  printBatchDelete: (batchId: string) => `/api/cards/print-batch/${batchId}`,
} as const;

/** Core Admin / Super Admin (auth, SUPER_ADMIN only unless @AllowTechAdmin). Base: /api/core-admin */
export const API_CORE_ADMIN = {
  dashboard: "/api/core-admin",
  dashboardSummary: "/api/core-admin/dashboard/summary",
  systemChecklist: "/api/core-admin/system-checklist",
  systemChecklistToggleRegistration: "/api/core-admin/system-checklist/toggle-registration",
  users: "/api/core-admin/users",
  usersPromote: "/api/core-admin/users/promote",
  usersDemote: "/api/core-admin/users/demote",
  usersRoleBulk: "/api/core-admin/users/role-bulk",
  usersSuspendBulk: "/api/core-admin/users/suspend-bulk",
  usersTrashBulk: "/api/core-admin/users/trash-bulk",
  userPermanentDelete: (userId: string) => `/api/core-admin/users/${userId}/permanent`,
  rolesPresets: "/api/core-admin/roles/presets",
  rolesApplyPreset: "/api/core-admin/roles/apply-preset",
  permissionsCatalog: "/api/core-admin/permissions/catalog",
  permissionsSubjects: "/api/core-admin/permissions/subjects",
  permissionsUser: (userId: string) => `/api/core-admin/permissions/user/${userId}`,
  permissionsAssignBulk: "/api/core-admin/permissions/assign-bulk",
  permissionsHistory: (principalId: string) => `/api/core-admin/permissions/history?principalId=${principalId}`,
  permissionsApplyPreset: "/api/core-admin/permissions/apply-preset",
  logs: "/api/core-admin/logs",
  settings: "/api/core-admin/settings",
  settingsGeneral: "/api/core-admin/settings/general",
  automations: "/api/core-admin/automations",
  invoicesTemplates: "/api/core-admin/invoices/templates",
  trash: "/api/core-admin/trash",
  trashRestore: (memberProfileId: string) => `/api/core-admin/trash/${memberProfileId}/restore`,
  trashPermanentDelete: (memberProfileId: string) => `/api/core-admin/trash/${memberProfileId}`,
  notifications: "/api/core-admin/notifications",
  verificationRules: "/api/core-admin/verification-rules",
  idRules: "/api/core-admin/id-rules",
  securityQuestions: "/api/core-admin/security-questions",
  email: "/api/core-admin/email",
  emailTemplates: "/api/core-admin/email/templates",
  emailTemplate: (key: string) => `/api/core-admin/email/templates/${key}`,
  recovery: "/api/core-admin/recovery",
  recoveryDetail: (code: string) => `/api/core-admin/recovery/${code}`,
  recoveryApprove: (code: string) => `/api/core-admin/recovery/${code}/approve`,
  recoveryReject: (code: string) => `/api/core-admin/recovery/${code}/reject`,
  recoveryExecute: (code: string) => `/api/core-admin/recovery/${code}/execute`,
  payments: "/api/core-admin/payments",
  coupons: "/api/core-admin/coupons",
  coupon: (id: string) => `/api/core-admin/coupons/${id}`,
  /** Plans & pricing (Super Admin). Amounts in paise on API; UI converts to/from rupees. */
  paymentsPlans: "/api/core-admin/payments/plans",
  paymentsPlanToggle: (planId: string) => `/api/core-admin/payments/plans/${planId}/toggle`,
  paymentsPlanUpdate: (planId: string) => `/api/core-admin/payments/plans/${planId}`,
  paymentsPlanDelete: (planId: string) => `/api/core-admin/payments/plans/${planId}`,
  paymentsGateway: "/api/core-admin/payments/gateway",
  paymentsProviderConfig: "/api/core-admin/payments/provider-config",
  templates: "/api/core-admin/templates",
  templateActivate: (id: string) => `/api/core-admin/templates/${id}/activate`,
  automationsRules: "/api/core-admin/automations/rules",
  automationsRuleUpdate: (id: string) => `/api/core-admin/automations/rules/${id}`,
  automationsRuleToggle: (id: string) => `/api/core-admin/automations/rules/${id}/toggle`,
  automationsDryRun: "/api/core-admin/automations/dry-run-preview",
  automationsDispatchLog: "/api/core-admin/automations/dispatch-log",
} as const;

/** Extract user-friendly error message from API error */
export function getApiErrorMessage(
  err: { status?: number; message?: string; body?: unknown },
  fallback = "Something went wrong. Please try again."
): string {
  const body = err?.body as { message?: string | string[]; error?: string } | undefined;
  if (body?.message) {
    const msg = Array.isArray(body.message) ? body.message.join(". ") : body.message;
    if (msg) return msg;
  }
  if (body?.error && typeof body.error === "string") return body.error;
  if (err?.message) return err.message;
  if (err?.status === 400) return "Invalid request. Please check your input.";
  if (err?.status === 401) return "Please sign in again.";
  if (err?.status === 403) return "You don't have permission to do this.";
  if (err?.status === 404) return "Not found.";
  return fallback;
}

const OTP_MSG = {
  invalidEmail: { EN: "Please enter a valid email address.", GU: "કૃપા કરીને માન્ય ઇમેઇલ ફોર્મેટ દાખલ કરો." },
  rateLimit: { EN: "Too many attempts. Please try again in a few minutes.", GU: "ખૂબ ઘણા પ્રયાસો. કૃપા કરીને થોડી મિનિટોમાં ફરી પ્રયાસ કરો." },
  cooldown: { EN: "Please wait a few minutes before requesting another code.", GU: "કૃપા કરીને બીજો કોડ વિનંતી કરતા પહેલા થોડી મિનિટો રાહ જુઓ." },
  network: { EN: "Please check your connection and try again.", GU: "કૃપા કરીને તમારું કનેક્શન ચકાસો અને ફરી પ્રયાસ કરો." },
  server: { EN: "We're having technical difficulties. Please try again later.", GU: "અમને ટેકનિકલ સમસ્યાઓ આવી રહી છે. કૃપા કરીને પછી ફરી પ્રયાસ કરો." },
  generic: { EN: "Could not send OTP. Please try again.", GU: "કોડ મોકલવામાં નિષ્ફળ. ફરી પ્રયાસ કરો." },
  emailAlreadyRegistered: { EN: "This email is already registered. Please sign in instead.", GU: "આ ઇમેઇલ પહેલેથી જ નોંધાયેલ છે. કૃપા કરીને સાઇન ઇન કરો." },
  accountSuspended: { EN: "Account is suspended. Please contact support.", GU: "એકાઉન્ટ સસ્પેન્ડ કર્યું છે. કૃપા કરીને સપોર્ટનો સંપર્ક કરો." },
  noAccountDownload: { EN: "No account found with this email for ID card download. Please use the email linked to your membership.", GU: "આ ઇમેઇલ સાથે ID કાર્ડ ડાઉનલોડ માટે કોઈ એકાઉન્ટ મળ્યું નથી. કૃપા કરીને તમારી સભ્યતા સાથે લિંક્ડ ઇમેઇલ વાપરો." },
  registrationClosed: { EN: "Registration is currently closed.", GU: "નોંધણી હાલમાં બંધ છે." },
  /** Nest unknown-route when API base path is wrong (e.g. missing /api/v1). */
  apiRoutingMismatch: {
    EN: "The app could not reach the server correctly. Please try again later or contact support.",
    GU: "એપ સર્વર સુધી યોગ્ય રીતે પહોંચી શકી નથી. કૃપા કરીને પછી ફરી પ્રયાસ કરો અથવા સપોર્ટનો સંપર્ક કરો.",
  },
} as const;

/** User-friendly error messages for OTP request (login/register Generate OTP / Request OTP). */
export function getOtpRequestErrorMessage(
  err: { status?: number; message?: string; body?: unknown },
  fallback = "Could not send OTP. Please try again.",
  lang: "EN" | "GU" = "EN"
): string {
  const t = (key: keyof typeof OTP_MSG) => OTP_MSG[key][lang];
  const errMsg = (err?.message ?? "").toLowerCase();
  if (errMsg.includes("failed to fetch") || errMsg.includes("networkerror") || errMsg.includes("load failed")) {
    return t("network");
  }
  const status = err?.status;
  if (status && status >= 500) return t("server");
  if (status === 429) return t("rateLimit");

  const body = err?.body as { message?: string | string[] } | undefined;
  const bodyMsg = body?.message
    ? (Array.isArray(body.message) ? body.message.join(" ") : String(body.message))
    : "";

  if (bodyMsg) {
    const lower = bodyMsg.toLowerCase();
    if (lower.includes("cannot post") || lower.includes("cannot get")) {
      return t("apiRoutingMismatch");
    }
    if (lower.includes("already registered")) return t("emailAlreadyRegistered");
    if (lower.includes("registration") && lower.includes("closed")) return t("registrationClosed");
    if (lower.includes("suspended")) return t("accountSuspended");
    if (lower.includes("no account found") && lower.includes("id card")) return t("noAccountDownload");
    if (lower.includes("wait a few minutes") || lower.includes("requesting another code")) return t("cooldown");
    if (lower.includes("too many attempts")) return t("rateLimit");
    if (lang === "EN") return bodyMsg;
    return t("generic");
  }

  if (status === 400 || status === 422) {
    const b = (bodyMsg || "").toLowerCase();
    if (b.includes("must be an email") || b.includes("must be a valid email")) return t("invalidEmail");
    return t("invalidEmail");
  }

  if (status === 403) {
    const lower = (bodyMsg || "").toLowerCase();
    if (lower.includes("registration") && lower.includes("closed")) return t("registrationClosed");
    return t("accountSuspended");
  }
  if (status === 404) return t("noAccountDownload");

  if (lang === "GU") return t("generic");
  return getApiErrorMessage(err, fallback);
}
